import React, {Component} from 'react';
import {connect} from 'react-redux';
import {FormattedMessage} from 'react-intl';
import get from 'lodash/get';
import classNames from 'classnames';
import {constants} from '@neosvoc/nv-platform-core-common';
import {
    requestMediaList, requestMediaUpload, requestMediaDelete, changeMediaListPage, changeMediaListFilter,
} from '../../actions/action_media_list';
import Loader from '../Loader';
import Pager from '../Pager';
import Title from '../Title';
import Alert from '../Alert';
import PaginationFilter from '../PaginationFilter';
import MediaAssetCard from './MediaAssetCard';
import messages from '../../../res/translations/messages';
import {userHasPermission} from '../../utils/auth';

const PAGE_SIZE = 12;

class MediaGalleryPage extends Component {
    constructor(props) {
        super(props);
        this.goToPage = this.goToPage.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleUpload = this.handleUpload.bind(this);
        this.handleApplyFilters = this.handleApplyFilters.bind(this);
        this.handleClearFilters = this.handleClearFilters.bind(this);
    }

    componentDidMount() {
        this.loadCurrentPage();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.page !== this.props.page || (prevProps.invalid !== this.props.invalid && this.props.invalid)) {
            this.loadCurrentPage();
        }

        if (this.props.page > 1 && this.props.items.length === 0) {
            this.props.changeMediaListPage(1);
        }
    }

    loadCurrentPage() {
        const {filterText, sortBy} = this.props;
        this.props.requestMediaList((this.props.page - 1) * PAGE_SIZE, PAGE_SIZE, filterText, sortBy);
    }

    goToPage(page) {
        this.props.changeMediaListPage(page);
    }

    handleDelete(id) {
        this.props.requestMediaDelete(id);
    }

    handleUpload(e) {
        const file = e.target.files[0];
        e.target.value = '';
        if (file) {
            this.props.requestMediaUpload(file);
        }
    }

    handleApplyFilters(filterText, sortBy) {
        this.props.changeMediaListFilter(filterText, sortBy);
        this.props.changeMediaListPage(1);
        this.props.requestMediaList(0, PAGE_SIZE, filterText, sortBy);
    }

    handleClearFilters() {
        this.props.changeMediaListFilter('', '');
        this.props.changeMediaListPage(1);
        this.props.requestMediaList(0, PAGE_SIZE, '', '');
    }

    render() {
        const {count, loading, error, uploading, uploadError, items, roles} = this.props;
        const canEdit = userHasPermission(roles, constants.PERMISSIONS.SURVEY_EDIT);

        return (
            <div className="container">
                <Title>
                    <FormattedMessage {...messages.media.title.list} />
                </Title>
                <div className="row mb-3 align-items-center">
                    <div className="col-auto">
                        <PaginationFilter
                            filterPlaceholder={messages.media.list.filterPlaceholder}
                            sortPlaceholder={messages.media.list.sort.placeholder}
                            sortOptions={[
                                {value: 'filename:asc', label: messages.media.list.sort.filename.asc},
                                {value: 'filename:desc', label: messages.media.list.sort.filename.desc},
                                {value: 'created_at:asc', label: messages.media.list.sort.created_at.asc},
                                {value: 'created_at:desc', label: messages.media.list.sort.created_at.desc},
                            ]}
                            onApply={this.handleApplyFilters}
                            onClear={this.handleClearFilters}
                            initialFilterText={this.props.filterText}
                            initialSortBy={this.props.sortBy}
                            applyLabel={messages.dashboard.buttons.apply}
                            resetLabel={messages.media.list.reset}
                        />
                    </div>
                    <div className="col ml-auto text-right">
                        {canEdit && (
                            <label className={classNames('btn btn-primary mb-0', {disabled: uploading})}>
                                <FormattedMessage {...(uploading ? messages.media.button.uploading : messages.media.button.upload)} />
                                <input type="file" accept="image/*" hidden disabled={uploading} onChange={this.handleUpload} />
                            </label>
                        )}
                    </div>
                </div>
                <div className="row mb-3 align-items-center">
                    <div className="col text-right">
                        <Pager className="justify-content-end" pages={Math.ceil(count / PAGE_SIZE)} current={this.props.page} onClick={this.goToPage} />
                    </div>
                </div>
                {loading ? <Loader /> : (
                    <>
                        <Alert error={error || uploadError} />
                        {count === 0 ? (
                            <p className="text-center text-muted"><FormattedMessage {...messages.media.list.empty} /></p>
                        ) : (
                            <div className="row">
                                {items.map((item) => (
                                    <MediaAssetCard key={item.id} item={item} canEdit={canEdit} onDelete={this.handleDelete} />
                                ))}
                            </div>
                        )}
                    </>
                )}
                <Pager className="justify-content-end" pages={Math.ceil(count / PAGE_SIZE)} current={this.props.page} onClick={this.goToPage} />
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        count: state.mediaList.count,
        page: state.mediaList.page,
        items: state.mediaList.items,
        loading: state.mediaList.loading,
        error: state.mediaList.error,
        invalid: state.mediaList.invalid,
        uploading: state.mediaList.uploading,
        uploadError: state.mediaList.uploadError,
        filterText: state.mediaList.filterText,
        sortBy: state.mediaList.sortBy,
        roles: get(state, 'auth.user.roles', []),
    };
};

export default connect(mapStateToProps,
    {requestMediaList, requestMediaUpload, requestMediaDelete, changeMediaListPage, changeMediaListFilter},
)(MediaGalleryPage);
